import { RefObject, useEffect, useState } from 'react';

type RefType = Element | null;

const isObserverAvailable = typeof window !== 'undefined' && 'IntersectionObserver' in window;

/**
 * @desc: хук для определения того, находится ли элемент во вьюпорте
 * @param ref
 * @returns {boolean}
 */
export const useOnScreen = (ref: RefObject<RefType>): boolean => {
  const [isIntersecting, setIntersecting] = useState(false);
  let observer: IntersectionObserver | null = null;
  if (isObserverAvailable) {
    observer = new IntersectionObserver(([entry]) => setIntersecting(entry.isIntersecting));
  }

  useEffect(() => {
    if (ref?.current && observer) {
      observer.observe(ref.current);
    }
    // Remove the observer as soon as the component is unmounted
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [ref]);

  return isIntersecting;
};

/**
 * the same like useOnScreen, but called once!
 * @param ref
 * @returns {boolean}
 */
export const useOnScreenOnce = (ref: RefObject<RefType>) => {
  const [isVisible, setState] = useState(false);

  useEffect(() => {
    let observerRefValue: RefType = null;
    let observer: IntersectionObserver | null = null;
    if (isObserverAvailable) {
      observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && ref.current) {
          setState(entry.isIntersecting);
          observer?.unobserve(ref.current);
        }
      });
    }

    if (ref.current) {
      observer?.observe(ref.current);
      observerRefValue = ref.current;
    }

    return () => {
      if (observerRefValue) {
        observer?.unobserve(observerRefValue);
      }
    };
  }, [ref]);

  return isVisible;
};
